import React, { FC, ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

/** A centred window for the client portal screens (rendered at the page root). */
export const PortalModal: FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}> = ({ title, subtitle, onClose, children, footer, width = 560 }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(31,36,51,0.45)] px-4 font-Manrope"
      }
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={title}
        className={
          "flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl bg-white text-[#1F2433] shadow-[0_20px_60px_rgba(31,36,51,0.25)]"
        }
        style={{ maxWidth: width }}>
        <div
          className={
            "flex items-start justify-between gap-4 border-b border-[#EDEFF3] px-6 py-5"
          }>
          <div className={"flex flex-col gap-1"}>
            <h2 className={"m-0 text-lg font-extrabold"}>{title}</h2>
            {subtitle && (
              <span className={"text-[13px] font-medium text-[#6B7185]"}>
                {subtitle}
              </span>
            )}
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "rounded-lg px-2 py-1 text-lg leading-none text-[#9097A8] hover:bg-[#F1F2F5]"
            }>
            ✕
          </button>
        </div>
        {/* [&>*]:shrink-0: in this scrolling column a child with its own overflow-hidden (a bordered list)
            was squeezed and cut off instead of the window scrolling (owner 2026-10-30, Notification settings). */}
        <div
          className={
            "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5 [&>*]:shrink-0"
          }>
          {children}
        </div>
        {footer && (
          <div
            className={
              "flex items-center justify-end gap-2.5 border-t border-[#EDEFF3] px-6 py-4"
            }>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export const modalCancel =
  "h-10 rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-4 text-[13.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA]";
export const modalPrimary =
  "h-10 rounded-[9px] bg-[#6B4EFF] px-5 text-[13.5px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:opacity-60";
export const modalInput =
  "h-10 w-full rounded-[9px] border-[1.5px] border-[#DCE2EA] px-3 text-[13.5px] outline-none placeholder:text-[#9097A8] focus:border-[#6B4EFF]";

export const errorText = (
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string => {
  const data = (error as { data?: unknown } | undefined)?.data;
  return typeof data === "string" && data.trim() && !data.trim().startsWith("<")
    ? data.trim()
    : fallback;
};
